import { FrameContexts } from "@microsoft/teams-js";
import { clsx } from "clsx";
import { DateTime } from "luxon";
import React, {
  type PropsWithChildren,
  useCallback,
  useEffect,
  useMemo,
  useState,
} from "react";
import LazyLoad from "react-lazy-load";
import { useDispatch, useSelector } from "react-redux";
import type { RootState } from "typesafe-actions";
import { DashboardOption } from "@/enums/DashboardOption.enum";
import { selectCanAssignProjectToWorkspace } from "../../../selectors/selectCanAssignProjectToWorkspace.selector";
import {
  config as selectConfig,
  featureFlags as selectFeatureFlags,
} from "../../../state/config/config.selector";
import type { AppState } from "../../../state/root.reducer";
import { userToken as selectUserToken } from "../../../state/user/user.selector";
import {
  createSelectCanRemoveProjectFromWorkspace,
  createSelectProjectByIdFillingUnknownOne,
} from "../../../state/workspaces/projects.selector";
import type { MyWorkspaceMetadata } from "../../../state/workspaces/types";
import * as workspacesActions from "../../../state/workspaces/workspaces.actions";
import {
  createSelectDashboardWorkspaceDetailsById,
  isWorkspaceLimited,
  selectDashboardActionOption,
  selectFullscreenWorkspaceId,
  selectSelectedWorkspaceId,
} from "../../../state/workspaces/workspaces.selector";
import { compareDeeply } from "../../../utils/comparators";
import hasNewTabInput from "../../../utils/hasNewTabInput";
import { useI18n } from "../../../utils/hooks/use.i18n";
import { isElectron } from "../../../utils/is.electron";
import useAutoVisibility from "../../hooks/useAutoVisibility";
import { MoreOptions } from "../../MoreOptions/MoreOptions";
import CustomWorkspacePreview from "./CustomWorkspacePreview";
import {
  WorkspaceCardContext,
  type WorkspaceCardContextPayload,
} from "./context/workspace.card.context";
import DefaultWorkspacePreview from "./DefaultWorkspacePreview";
import Styles from "./WorkspaceCard.module.css";
import WorkspaceCardThumbnail from "./WorkspaceCardThumbnail";

export type MenuItemSettings = {
  icon: string;
  label: string;
  onClick: () => void;
  title?: string;
};

export interface WorkspaceCardProps {
  myWorkspaceMetadata: MyWorkspaceMetadata;
  editWorkspaceAction?: (workspaceId: string) => void;
  isFavorite?: boolean;
}

const shouldRerender = (
  prevProps: PropsWithChildren<WorkspaceCardProps>,
  nextProps: PropsWithChildren<WorkspaceCardProps>,
) => {
  return compareDeeply(nextProps, prevProps);
};

const ellipsis = "…";

export const WorkspaceCard: React.FC<WorkspaceCardProps> = React.memo(
  ({ editWorkspaceAction, isFavorite = false, myWorkspaceMetadata }) => {
    const dispatch = useDispatch();

    // Create stable selector function instances.
    const selectGetProjectByIdFillingUnknownOne = useMemo(
      createSelectProjectByIdFillingUnknownOne,
      [],
    );
    const selectCanRemoveProjectFromWorkspace = useMemo(
      createSelectCanRemoveProjectFromWorkspace,
      [],
    );
    const selectDashboardWorkspaceDetailsById = useMemo(
      createSelectDashboardWorkspaceDetailsById,
      [],
    );

    const t = useI18n("WORKSPACE_CARD.");

    const config = useSelector(selectConfig);
    const token = useSelector(selectUserToken);
    const {
      enableWorkspaceExport: isWorkspaceExportEnabled,
      showWorkspaceWeight,
    } = useSelector(selectFeatureFlags);
    const project = useSelector((state: AppState) =>
      selectGetProjectByIdFillingUnknownOne(
        state,
        myWorkspaceMetadata.containerId,
      ),
    );

    const dashboardWorkspaceDetail = useSelector((state: AppState) =>
      selectDashboardWorkspaceDetailsById(
        state,
        myWorkspaceMetadata.workspaceId,
      ),
    );
    const fullscreenWorkspaceId = useSelector(selectFullscreenWorkspaceId);
    const isLimited = useSelector((state: RootState) =>
      isWorkspaceLimited(state, myWorkspaceMetadata.workspaceId),
    );

    const canAssignProject = useSelector((state: AppState) =>
      selectCanAssignProjectToWorkspace(state, myWorkspaceMetadata),
    );
    const canRemoveProject = useSelector((state: RootState) =>
      selectCanRemoveProjectFromWorkspace(
        state,
        myWorkspaceMetadata.containerId,
      ),
    );

    const isOptionsMenuOpen = useSelector((state: AppState) => {
      const selectedWorkspaceId = selectSelectedWorkspaceId(state);
      const activeDashboardOption = selectDashboardActionOption(state);
      const openOptions = new Set([
        DashboardOption.OPTIONS,
        DashboardOption.OPTIONS_FAVORITE,
      ]);

      return !!(
        activeDashboardOption &&
        openOptions.has(activeDashboardOption) &&
        selectedWorkspaceId === myWorkspaceMetadata.workspaceId
      );
    });

    const isFullscreen =
      myWorkspaceMetadata.workspaceId === fullscreenWorkspaceId;

    const allowFullScreen = useMemo(
      () => isFullscreen && config.teamsFrame !== FrameContexts.sidePanel,
      [isFullscreen, config.teamsFrame],
    );
    const applyOpacityStyle = project?.status === "Inactive" && !isFullscreen;
    const isTemplate = !!myWorkspaceMetadata.templateId;

    const [isVisible, setIsVisible] = useState(false);

    const { ref, outsideElementRef, componentVisible, setComponentVisible } =
      useAutoVisibility(false);

    const closeOptionsMenu = useCallback(() => {
      dispatch(workspacesActions.cancelDashboardOption());
    }, [dispatch]);

    const setActiveDashboardOption = useCallback(
      (optionType: DashboardOption) =>
        dispatch(
          workspacesActions.activateDashboardOption({
            optionType,
            workspaceId: myWorkspaceMetadata.workspaceId,
          }),
        ),
      [dispatch, myWorkspaceMetadata.workspaceId],
    );

    const createSetActiveDashboardOptionCallback = useCallback(
      (optionType: DashboardOption) => () =>
        setActiveDashboardOption(optionType),
      [setActiveDashboardOption],
    );

    useEffect(() => {
      if (!componentVisible && isOptionsMenuOpen) {
        closeOptionsMenu();
      }
    }, [closeOptionsMenu, componentVisible, isOptionsMenuOpen]);

    const canEditLabels = myWorkspaceMetadata.myPermissions.canAdmin;

    const showEditLabelPrompt = createSetActiveDashboardOptionCallback(
      DashboardOption.ADD_LABEL,
    );

    const openWorkspace = (w: MyWorkspaceMetadata, newTab: boolean) => {
      if (
        (w.workspaceType === "HOYLU" || w.workspaceType === "EXPERIMENTAL") &&
        !newTab
      ) {
        if (editWorkspaceAction) {
          editWorkspaceAction(w.workspaceId);
        } else {
          dispatch(workspacesActions.editWorkspace(w));
        }
      } else {
        dispatch(
          workspacesActions.setLastAccess({
            workspaceId: w.workspaceId,
            lastAccess: DateTime.utc().toISO(),
          }),
        );
        window.open(w.workspaceId, w.workspaceId);
      }
    };

    const openWorkspaceLink = (e: React.MouseEvent) => {
      e.preventDefault();
      // only left click and middle click can be used to open workspace
      if ([0, 1].includes(e.button)) {
        if (isTemplate) {
          dispatch(
            workspacesActions.createWorkspaceFromTemplate(myWorkspaceMetadata),
          );
          return;
        }
        // workspace will open in a new tab if holding action button or using middle click
        openWorkspace(myWorkspaceMetadata, hasNewTabInput(e) && !isElectron());
      }
    };

    const openMoreOptions = (
      e: React.MouseEvent<HTMLButtonElement, MouseEvent>,
    ) => {
      const optionType = isFavorite
        ? DashboardOption.OPTIONS_FAVORITE
        : DashboardOption.OPTIONS;

      e.stopPropagation();
      setComponentVisible(true);
      setActiveDashboardOption(optionType);
    };

    const showWorkspaceModifiedNotification =
      myWorkspaceMetadata.lastAccess &&
      myWorkspaceMetadata.lastModified &&
      myWorkspaceMetadata.lastModified
        ? myWorkspaceMetadata.lastAccess < myWorkspaceMetadata.lastModified
        : false;

    const menuItems = useMemo(() => {
      const showInfoModal = createSetActiveDashboardOptionCallback(
        DashboardOption.INFO,
      );

      const editWorkspaceTemplate = () => {
        closeOptionsMenu();
        if (myWorkspaceMetadata.myPermissions.canAdmin) {
          dispatch(
            workspacesActions.queueEditWorkspace({
              workspaceId: myWorkspaceMetadata.workspaceId,
              allowEditTemplates: true,
            }),
          );
        }
      };

      const menuItems: MenuItemSettings[] = [];

      menuItems.push(
        {
          icon: "hoylu-ui-icons-blank",
          label: t("WORKSPACE_INFO"),
          onClick: showInfoModal,
        },
        {
          icon: "hoylu-ui-icons-share",
          label: t("SHARE"),
          title: t("SHARE_SETTINGS"),
          onClick: createSetActiveDashboardOptionCallback(
            DashboardOption.SHARE,
          ),
        },
      );

      if (
        myWorkspaceMetadata.myPermissions.canAdmin &&
        myWorkspaceMetadata.templateId
      ) {
        menuItems.push({
          icon: "hoylu-ui-icons-section-template",
          label: t("EDIT_TEMPLATE"),
          title: t("EDIT_TEMPLATE_TOOLTIP"),
          onClick: editWorkspaceTemplate,
        });
      }

      if (myWorkspaceMetadata.myPermissions.canAdmin) {
        menuItems.push({
          icon: "hoylu-ui-icons-labels-edit",
          label: t("ADD_LABEL"),
          title: t("LABELS"),
          onClick: showEditLabelPrompt,
        });
      }

      if (canAssignProject) {
        menuItems.push({
          icon: "hoylu-ui-icons-box",
          label: t("ASSIGN_PROJECT"),
          onClick: showInfoModal,
        });
      }

      if (canRemoveProject) {
        menuItems.push({
          icon: "hoylu-ui-icons-incomplete",
          label: t("REMOVE_PROJECT"),
          onClick: createSetActiveDashboardOptionCallback(
            DashboardOption.REMOVE_FROM_PROJECT,
          ),
        });
      }

      if (isWorkspaceExportEnabled) {
        menuItems.push({
          icon: "hoylu-ui-icons-import",
          label: t("DOWNLOAD_ARCHIVE"),
          onClick: createSetActiveDashboardOptionCallback(
            DashboardOption.DOWNLOAD,
          ),
        });
      }

      if (
        myWorkspaceMetadata.workspaceType === "HOYLU" &&
        !myWorkspaceMetadata.templateId
      ) {
        menuItems.push({
          icon: "hoylu-ui-icons-copy",
          label: `${t("DUPLICATE")}${ellipsis}`,
          title: t("DUPLICATE_WORKSPACE"),
          onClick: createSetActiveDashboardOptionCallback(
            DashboardOption.DUPLICATE,
          ),
        });
      }

      if (
        !myWorkspaceMetadata.containerId &&
        dashboardWorkspaceDetail?.isFromMyWorkspaces
      ) {
        const isHidden = myWorkspaceMetadata.isHidden;

        menuItems.push({
          icon: isHidden
            ? "hoylu-ui-icons-indicator-show"
            : "hoylu-ui-icons-indicator-hide",
          label: `${isHidden ? t("UNHIDE") : t("HIDE")}${ellipsis}`,
          title: isHidden ? t("UNHIDE_WORKSPACE") : t("HIDE_WORKSPACE"),
          onClick: isHidden
            ? () =>
                dispatch(
                  workspacesActions.toggleWorkspaceVisibility.request(
                    myWorkspaceMetadata,
                  ),
                )
            : createSetActiveDashboardOptionCallback(DashboardOption.HIDE),
        });
      }

      if (myWorkspaceMetadata.myPermissions.canLeave) {
        menuItems.push({
          icon: "hoylu-ui-icons-leave",
          label: `${t("LEAVE")}${ellipsis}`,
          title: t("LEAVE_WORKSPACE"),
          onClick: createSetActiveDashboardOptionCallback(
            DashboardOption.LEAVE,
          ),
        });
      }

      if (
        myWorkspaceMetadata.myPermissions.canAdmin &&
        !myWorkspaceMetadata.templateId
      ) {
        menuItems.push({
          icon: "hoylu-ui-icons-trash",
          label: `${t("DELETE")}${ellipsis}`,
          title: t("DELETE_WORKSPACE"),
          onClick: createSetActiveDashboardOptionCallback(
            DashboardOption.DELETE,
          ),
        });
      }

      return menuItems;
    }, [
      canAssignProject,
      canRemoveProject,
      closeOptionsMenu,
      createSetActiveDashboardOptionCallback,
      dashboardWorkspaceDetail,
      dispatch,
      isWorkspaceExportEnabled,
      myWorkspaceMetadata,
      showEditLabelPrompt,
      t,
    ]);

    const workspaceModifiedNotificationText = showWorkspaceModifiedNotification
      ? t("CHANGES_SINCE")
      : t("NO_CHANGES_SINCE");

    const workspaceCardContextValue = useMemo<WorkspaceCardContextPayload>(
      () => ({ isFavorite }),
      [isFavorite],
    );

    return (
      <WorkspaceCardContext.Provider value={workspaceCardContextValue}>
        <div className={Styles.workspaceCardContainer}>
          <div
            className={clsx(
              Styles.workspaceCard,
              applyOpacityStyle && Styles.inactive,
            )}
            data-workspace-name={myWorkspaceMetadata.workspaceName}
            title={`${myWorkspaceMetadata.workspaceName}\n${
              isTemplate ? t("TEMPLATE") : workspaceModifiedNotificationText
            }`}
          >
            <div
              className={clsx(!allowFullScreen && Styles.workspaceTile)}
              data-test-id="workspace-tile"
              /* NOTE: We use two methods here because onClick allows to open EditLabelDialog while clicking on workspace label,
                 instead of opening Workspace directly. However, this handler does not capture mouse middle click, blocking
                 opening the workspace with it. onMouseDown was used explicitly to handle the middle click.
               */
              onClick={openWorkspaceLink}
              onMouseDown={(e) =>
                e.button === 1 ? openWorkspaceLink(e) : null
              }
            >
              <LazyLoad>
                <CustomWorkspacePreview
                  canEditLabels={canEditLabels}
                  myWorkspaceMetadata={myWorkspaceMetadata}
                  setIsVisible={setIsVisible}
                  showEditLabelPrompt={showEditLabelPrompt}
                  showWorkspaceWeight={showWorkspaceWeight}
                  token={token}
                />
              </LazyLoad>
              {!isVisible && (
                <DefaultWorkspacePreview
                  canEditLabels={canEditLabels}
                  myWorkspaceMetadata={myWorkspaceMetadata}
                  showEditLabelPrompt={showEditLabelPrompt}
                  showWorkspaceWeight={showWorkspaceWeight}
                />
              )}
            </div>
            <WorkspaceCardThumbnail
              config={config}
              isLimited={isLimited}
              isOptionsMenuOpen={isOptionsMenuOpen}
              myWorkspaceMetadata={myWorkspaceMetadata}
              openMoreOptions={openMoreOptions}
              // This will require change on client-common package to allow null values.
              outsideElementRef={
                outsideElementRef as React.RefObject<HTMLDivElement>
              }
              project={project}
              showWorkspaceModifiedNotification={
                showWorkspaceModifiedNotification
              }
            />
          </div>
          {isOptionsMenuOpen && (
            <div className={Styles.moreOptionsContainer} ref={ref}>
              <MoreOptions menuItems={menuItems} />
            </div>
          )}
        </div>
      </WorkspaceCardContext.Provider>
    );
  },
  shouldRerender,
);

export default WorkspaceCard;
